/**
 * Playground League Pro - Branded HTML Email Templates
 * Styled with Tailwind CSS design tokens (inline styles for universal email client compatibility)
 */

export interface VerificationEmailTemplateProps {
  recipientEmail: string;
  recipientName?: string;
  verificationCode: string;
  directVerifyUrl: string;
  appOrigin?: string;
  year?: number;
}

export const TEMPLATE_VARIABLES = [
  { key: '{{recipientName}}', label: 'Athlete Name', description: 'Full name or "Athlete" fallback' },
  { key: '{{recipientEmail}}', label: 'Athlete Email', description: 'Destination recipient email address' },
  { key: '{{verificationCode}}', label: '6-Digit OTP', description: 'The numeric one-time security code (e.g. 849201)' },
  { key: '{{directVerifyUrl}}', label: 'Direct Verify URL', description: '1-click verification link with embedded token' },
  { key: '{{appOrigin}}', label: 'App Base URL', description: 'Platform root domain (e.g. playgroundleague.pro)' },
  { key: '{{year}}', label: 'Current Year', description: 'Calendar year for copyright notice' },
];

export const DEFAULT_VERIFICATION_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Playground League - Athlete Verification</title>
  <!--[if mso]>
  <style type="text/css">
    body, table, td {font-family: Arial, Helvetica, sans-serif !important;}
  </style>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #030712; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; -webkit-font-smoothing: antialiased;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color: #030712; padding: 32px 16px;">
    <tr>
      <td align="center">
        <!-- MAIN CARD CONTAINER (max-width: 600px) -->
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width: 600px; background: #0b0f19; border: 1px solid #1e293b; border-radius: 28px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.75);">
          
          <!-- TOP GLOW ACCENT BAR -->
          <tr>
            <td height="6" style="background: linear-gradient(90deg, #a3e635 0%, #38bdf8 50%, #f59e0b 100%); line-height: 6px; font-size: 6px;">&nbsp;</td>
          </tr>

          <!-- BRAND HEADER HERO -->
          <tr>
            <td style="background: linear-gradient(180deg, #1e1b4b 0%, #0f172a 100%); padding: 36px 32px 28px 32px; text-align: center; border-bottom: 1px solid #1e293b;">
              
              <!-- LEAGUE BADGE -->
              <div style="display: inline-block; background-color: #a3e635; color: #000000; font-weight: 900; font-style: italic; padding: 6px 18px; border-radius: 9999px; font-size: 12px; text-transform: uppercase; letter-spacing: 1.5px; box-shadow: 0 4px 14px rgba(163, 230, 53, 0.4); margin-bottom: 16px;">
                ⚡ PLAYGROUND LEAGUE PRO
              </div>

              <h1 style="color: #ffffff; font-size: 26px; font-weight: 900; font-style: italic; text-transform: uppercase; letter-spacing: -0.5px; line-height: 1.2; margin: 0 0 8px 0;">
                ATHLETE IDENTITY VERIFICATION
              </h1>

              <p style="color: #94a3b8; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; margin: 0;">
                Official Pick-up & Tournament Network
              </p>
            </td>
          </tr>

          <!-- MAIN CONTENT BODY -->
          <tr>
            <td style="padding: 36px 32px 24px 32px; color: #cbd5e1;">
              
              <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0; color: #f8fafc;">
                Hey <strong style="color: #a3e635; font-size: 17px;">{{recipientName}}</strong>,
              </p>

              <p style="font-size: 14px; line-height: 1.65; color: #94a3b8; margin: 0 0 24px 0;">
                We received a request to verify your official athlete account associated with <strong style="color: #ffffff; text-decoration: underline;">{{recipientEmail}}</strong>. Please confirm your email address to unlock competitive leaderboards, create pickup runs, and enter regional tournaments.
              </p>

              <!-- 6-DIGIT SECURITY OTP CARD -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin: 28px 0; background: linear-gradient(135deg, #090d16 0%, #1e1b4b 100%); border: 2px dashed #a3e635; border-radius: 22px;">
                <tr>
                  <td style="padding: 24px; text-align: center;">
                    <span style="display: block; color: #a3e635; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; font-weight: 900; margin-bottom: 8px;">
                      🔒 ONE-TIME 6-DIGIT VERIFICATION CODE
                    </span>
                    <div style="font-size: 44px; font-weight: 900; letter-spacing: 12px; color: #a3e635; font-family: 'Courier New', Courier, monospace; line-height: 1.1; margin: 8px 0; text-shadow: 0 0 20px rgba(163, 230, 53, 0.45);">
                      {{verificationCode}}
                    </div>
                    <p style="color: #64748b; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; margin: 8px 0 0 0;">
                      ⏱️ Valid for 15 minutes • Single-use athlete security token
                    </p>
                  </td>
                </tr>
              </table>

              <!-- HIGH-CONVERSION CTA BUTTON -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin: 28px 0 20px 0;">
                <tr>
                  <td align="center">
                    <a href="{{directVerifyUrl}}" target="_blank" style="display: inline-block; background-color: #a3e635; color: #000000; font-weight: 900; font-style: italic; text-transform: uppercase; letter-spacing: 0.5px; font-size: 15px; padding: 18px 40px; border-radius: 16px; text-decoration: none; box-shadow: 0 10px 25px rgba(163, 230, 53, 0.35); text-align: center;">
                      ⚡ VERIFY ATHLETE PROFILE INSTANTLY &rarr;
                    </a>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-top: 12px;">
                    <span style="color: #64748b; font-size: 12px; font-weight: 500;">
                      Or enter the 6-digit code <strong style="color: #a3e635;">{{verificationCode}}</strong> directly in the app banner.
                    </span>
                  </td>
                </tr>
              </table>

              <!-- UNLOCKED FEATURES SUMMARY TABLE -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin: 28px 0 12px 0; background-color: #030712; border: 1px solid #1e293b; border-radius: 18px; padding: 18px;">
                <tr>
                  <td>
                    <div style="font-size: 11px; font-weight: 900; color: #a3e635; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 12px;">
                      🔥 What Unlocks Upon Email Verification:
                    </div>
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                      <tr>
                        <td style="padding: 6px 0; color: #cbd5e1; font-size: 13px;">
                          <span style="color: #a3e635; font-weight: bold; margin-right: 8px;">✓</span>
                          <strong>Official Leaderboard Rankings:</strong> Log points, rebounds, and tournament MVPs.
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 6px 0; color: #cbd5e1; font-size: 13px;">
                          <span style="color: #a3e635; font-weight: bold; margin-right: 8px;">✓</span>
                          <strong>Host Pickup Runs:</strong> Create custom public and private game lobbies at verified parks.
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 6px 0; color: #cbd5e1; font-size: 13px;">
                          <span style="color: #a3e635; font-weight: bold; margin-right: 8px;">✓</span>
                          <strong>Scout & Recruiter Visibility:</strong> Share unredacted stat cards with D1/FIBA scouts.
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- SECURITY & COMPLIANCE FOOTER -->
          <tr>
            <td style="background-color: #050811; padding: 28px 32px; border-top: 1px solid #1e293b; text-align: center;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                <tr>
                  <td align="center" style="padding-bottom: 12px;">
                    <span style="display: inline-block; background-color: rgba(163, 230, 53, 0.1); border: 1px solid rgba(163, 230, 53, 0.3); color: #a3e635; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; padding: 4px 12px; border-radius: 9999px;">
                      🛡️ SPF / DKIM / DMARC VERIFIED DISPATCH
                    </span>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="color: #64748b; font-size: 12px; line-height: 1.6;">
                    Dispatched securely by <strong style="color: #94a3b8;">Playground League Operations</strong><br>
                    Official Support: <a href="mailto:support@playgroundleague.pro" style="color: #a3e635; text-decoration: none; font-weight: 600;">support@playgroundleague.pro</a> • Web: <a href="{{appOrigin}}" style="color: #38bdf8; text-decoration: none;">playgroundleague.pro</a>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-top: 16px; color: #475569; font-size: 11px;">
                    © {{year}} Playground League Pro Inc. All rights reserved.<br>
                    If you did not register for Playground League, you can safely disregard this email.
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

/**
 * Replaces placeholders in the HTML template string with runtime variables.
 * Supports {{key}}, {{ key }}, and ${key} syntax.
 */
export function renderVerificationEmailHtml(
  templateSource: string,
  {
    recipientEmail,
    recipientName = 'Athlete',
    verificationCode,
    directVerifyUrl,
    appOrigin = 'https://playgroundleague.pro',
    year = new Date().getFullYear(),
  }: VerificationEmailTemplateProps
): string {
  let output = templateSource || DEFAULT_VERIFICATION_EMAIL_TEMPLATE;

  const replacements: Record<string, string> = {
    recipientEmail: recipientEmail || '',
    recipientName: recipientName || 'Athlete',
    verificationCode: verificationCode || '',
    directVerifyUrl: directVerifyUrl || '#',
    appOrigin: appOrigin || 'https://playgroundleague.pro',
    year: String(year || new Date().getFullYear()),
  };

  Object.entries(replacements).forEach(([key, val]) => {
    // Replace {{key}} and {{ key }}
    const doubleCurlyRegex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    output = output.replace(doubleCurlyRegex, val);

    // Replace ${key} or ${ key }
    const dollarCurlyRegex = new RegExp(`\\$\\{\\s*${key}\\s*\\}`, 'g');
    output = output.replace(dollarCurlyRegex, val);
  });

  return output;
}

export function generateBrandedVerificationEmailHtml(
  props: VerificationEmailTemplateProps,
  customTemplateSource?: string
): string {
  return renderVerificationEmailHtml(customTemplateSource || DEFAULT_VERIFICATION_EMAIL_TEMPLATE, props);
}

export interface WelcomeEmailTemplateProps {
  recipientEmail: string;
  recipientName?: string;
  sport?: string;
  isPro?: boolean;
  directVerifyUrl?: string;
  appOrigin?: string;
  year?: number;
}

export function generateBrandedWelcomeEmailHtml({
  recipientEmail,
  recipientName = 'Athlete',
  sport = 'Basketball',
  isPro = false,
  directVerifyUrl,
  appOrigin = 'https://playgroundleague.pro',
  year = new Date().getFullYear(),
}: WelcomeEmailTemplateProps): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to Playground League</title>
</head>
<body style="margin: 0; padding: 0; background-color: #030712; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color: #030712; padding: 32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width: 600px; background: #0b0f19; border: 1px solid #1e293b; border-radius: 28px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.75);">
          
          <tr>
            <td height="6" style="background: linear-gradient(90deg, #a3e635 0%, #f59e0b 100%); line-height: 6px; font-size: 6px;">&nbsp;</td>
          </tr>

          <tr>
            <td style="background: linear-gradient(180deg, #1e1b4b 0%, #0f172a 100%); padding: 36px 32px 28px 32px; text-align: center; border-bottom: 1px solid #1e293b;">
              <div style="display: inline-block; background-color: #a3e635; color: #000000; font-weight: 900; font-style: italic; padding: 6px 18px; border-radius: 9999px; font-size: 12px; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 14px;">
                🏀 PLAYGROUND LEAGUE
              </div>
              <h1 style="color: #ffffff; font-size: 26px; font-weight: 900; font-style: italic; text-transform: uppercase; margin: 0;">
                WELCOME TO THE LEAGUE, ${recipientName.toUpperCase()}!
              </h1>
              <p style="color: #94a3b8; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; margin: 8px 0 0 0;">
                Primary Sport: ${sport}
              </p>
            </td>
          </tr>

          <tr>
            <td style="padding: 32px; color: #cbd5e1;">
              <p style="font-size: 15px; line-height: 1.6; margin: 0 0 16px 0; color: #f8fafc;">
                Your athlete profile is officially registered! You are now plugged into local parks, pickup runs, stats leaderboards, and scouting analytics.
              </p>

              ${isPro ? `
                <div style="background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%); border: 2px solid #fbbf24; border-radius: 18px; padding: 20px; margin: 20px 0; text-align: center;">
                  <span style="color: #fbbf24; font-size: 13px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px;">👑 PLAYGROUND PRO MEMBERSHIP ACTIVE</span>
                  <p style="color: #e0e7ff; font-size: 13px; margin: 6px 0 0 0;">You have unlocked 1080p highlight reels, radar charts, priority reservations, and Gold Verified status!</p>
                </div>
              ` : ''}

              ${directVerifyUrl ? `
                <div style="text-align: center; margin: 28px 0;">
                  <a href="${directVerifyUrl}" target="_blank" style="display: inline-block; background-color: #a3e635; color: #000000; font-weight: 900; font-style: italic; text-transform: uppercase; padding: 16px 36px; border-radius: 14px; text-decoration: none; font-size: 14px;">
                    ⚡ Verify Your Athlete Email Now
                  </a>
                </div>
              ` : ''}
            </td>
          </tr>

          <tr>
            <td style="background-color: #050811; padding: 24px 32px; border-top: 1px solid #1e293b; text-align: center; color: #64748b; font-size: 12px;">
              © ${year} Playground League Pro • <a href="mailto:support@playgroundleague.pro" style="color: #a3e635; text-decoration: none;">support@playgroundleague.pro</a>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
